<script lang="ts" setup>
import { computed, ref, toRef, unref, watch, watchEffect } from 'vue'
import { t } from '@/locales/i18n'
import { useStore } from 'vuex'
import { useGetHand } from '@gto/api/vue-query'
import { getScreenUtils } from '@/components/lib/GwUtils/utils_screen'
import { Portal, PortalTarget } from 'portal-vue'
import { links_getUrl } from '@/composables/linksMixMethods'
import { useLinks } from '@/composables/linksMix'
import { getCaptureUtils } from '@/composables/captureMixMethods'
import { getClipboardUtils } from '@/composables/clipboardMixMethods'
import { getGmfUtils } from '@/composables/gmfMixMethods'
import { numFixedUnit } from '@gto/helpers/number-formatting'
// @ts-expect-error - I dunno why but TS complains about this import
import { getGameformat } from '@/store/gameformat/gameformat'
import {
  getCompleteHistorySpots,
  getHistorySpots,
  getGroupFoldHistorySpots,
  setHistoryEventType,
  getHSpotsUrlParamsFromSolvedSequece,
} from '@/store/gamestate/gamestate'
import {
  getCardsTillStreet,
  getStreetsByBoard,
  getCardsByStreet,
  getStreetIdx,
  getStreetByBoard,
} from '@/helpers/cards'
import { getHandCorrectness, getHandStats } from '@/helpers/trainer'
import GwMenuNew from '@/components/lib/GwMenuNew/GwMenuNew.vue'
import GwTooltipNew from '@/components/lib/GwTooltip/GwTooltipNew.vue'
import PartiallyAnalyzedHand from '@/components/Notices/PartiallyAnalyzedHand.vue'
import TrainerReplay from '@/components/Trainer/TrainerReplay.vue'
import HaGameformat from '@/components/AnalyzeStreetBlocks/HaGameformat.vue'
import HaTitle from '@/components/AnalyzeStreetBlocks/HaTitle.vue'
import AnalyzeStreetBlocks from '@/components/AnalyzeStreetBlocks/AnalyzeStreetBlocks.vue'
import ExpandCard from '@/components/Common/ExpandCard/ExpandCard.vue'
import GwMessage from '@/components/Messages/GwMessage.vue'
import HandDetailLogo from '@/components/hhv3/Analyzer/HandDetail/HandDetailLogo.vue'
import HandDetailError from '@/components/hhv3/Analyzer/HandDetail/HandDetailError.vue'
import HandDetailTitle from '@/components/hhv3/Analyzer/HandDetail/HandDetailTitle.vue'
import * as R from 'remeda'
import HaControls from '../../../AnalyzeStreetBlocks/HaControls.vue'
import LoaderCircles from '../../../Loaders/LoaderCircles.vue'
import HaPlayers from '../../../AnalyzeStreetBlocks/HaPlayers.vue'

const store = useStore()

export interface HandDetailProps {
  handId: string
}

const props = defineProps<HandDetailProps>()
const { mobile } = getScreenUtils()
const { capt_inProgress, capt_capture } = getCaptureUtils()
const { clipboard_copyToClipboard, clipboard_copied } = getClipboardUtils()
const { gmf_fetchGmfAndReturnStatus, gmf_loading } = getGmfUtils()

const handDetailMenuRef = ref<InstanceType<typeof GwMenuNew> | null>(null)

const handId = toRef(props, 'handId')
// @ts-ignore temporary fix wait for https://github.com/vuejs/core/issues/12986
const { data, isLoading, isFetching, refetch, isError, error: rqError } = useGetHand(handId)

const analyzeItem = ref()
const lastSpotLink = ref()
const historyHand = ref()
const gmfQuery = ref()

const replayNamespace = 'tmp/extra3'
const panelReplay = computed(() => store.getters.panel_replay)
// const handDetailItem = computed(() => store.getters.globals_drawer?.item || {})
const replayedSpot = computed(() => store.getters[`${replayNamespace}/history_spot`])

const getMappedPlayer = player => (player ? { ...player, amount_won: player.amount_net_won } : null)

const dataHand = computed(() => data.value)
const gameAnalysis = computed(() => dataHand.value?.game_analysis)
const warningStatus = computed(() => gameAnalysis.value?.warning_status)
const players = computed(() => dataHand.value?.players || [])
const hero = computed(() => getMappedPlayer(players.value.find(p => p.is_hero)))
const heroHand = computed(() => hero.value?.hand || '')
const heroPosition = computed(() => hero.value?.position)
const opponent = computed(() => getMappedPlayer(players.value.find(p => p.is_opponent)))
const opponentHand = computed(() => opponent.value?.hand || '')
const opponentPosition = computed(() => opponent.value?.position)
const boards = computed(() => dataHand.value?.boards || [])
const board = computed(() => dataHand.value?.boards[0] || '')
const blinds = computed(() => dataHand.value?.blinds)
const loading = computed(() => Boolean(unref(mobile) && unref(isFetching)))
// BE-fix - keep till extra showdown spot in gamepoints is fixed
const gamePoints = computed(() => {
  const res = gameAnalysis.value?.game_points || []
  // const res = (gameAnalysis.value?.game_points || []).filter(i => !!i.real_game_action)
  // console.log(JSON.parse(JSON.stringify(gameAnalysis.value?.game_points)))
  // console.log(JSON.parse(JSON.stringify(res)))
  return res
})

const firstGamePoint = computed(() => gamePoints.value[0])

const mapHistorySpotsWithBoardDetails = ({
  hSpots,
  solved_streets,
  real_streets,
  live_solved_street,
  live_solved_depth,
  solved_depth,
  all_solved_stacks,
  all_boards,
  first_board,
}) => {
  let res = hSpots

  const liveStreetIdx = getStreetIdx(live_solved_street)
  res = res.map((spot, idx) => {
    if (spot.type === 'board') {
      const nextSpot = res[idx + 1]
      const solved_street = solved_streets.find(s => s.street.toLowerCase() === spot.street)
      const real_street = real_streets.find(s => s.street.toLowerCase() === spot.street)

      const real_start_pot = spot.start_pot
      const start_pot = Number(solved_street?.start_pot) || Number(real_street?.start_pot)

      const d = {
        live_solved_depth: null,
        solved_depth,
        solved_stacks: all_solved_stacks?.[nextSpot.id],
        start_pot,
        real_start_pot,
      }
      const streetIdx = getStreetIdx(spot.street)
      if (streetIdx >= liveStreetIdx) {
        d.live_solved_depth = live_solved_depth
      }
      const active_players = spot.players.filter(p => !p.is_folded).length
      const r = {
        ...spot,
        ...d,
        active_players,
        urlParams: nextSpot?.urlParams,
      }
      return r
    }
    return spot
  })

  // add missing board street spots
  const streets = getStreetsByBoard(first_board)
  const allBoardSpots = res.filter(e => e.type === 'board')
  const missingBoards = streets.filter(i => !allBoardSpots.some(e => e.street === i))
  if (missingBoards.length) {
    // @ts-expect-error
    const lastBoardSpot: object = R.findLast(res, i => i.type === 'board')
    let missingBoardSpots = all_boards.map(l_board => {
      const r = missingBoards.map(street => {
        let l_live_solved_depth = null
        const streetIdx = getStreetIdx(street)
        if (streetIdx >= liveStreetIdx) {
          l_live_solved_depth = live_solved_depth
        }

        // console.log('lastBoardSpot', lastBoardSpot)
        const streetBoard = getCardsByStreet(l_board, street)
        const rr = {
          ...lastBoardSpot,
          type: 'board',
          board: streetBoard,
          id: street,
          street,
          live_solved_depth: l_live_solved_depth,
          urlParams: null,
        }
        return rr
      })
      return r
    })

    let showdownSpot = res.find(i => i.type === 'showdown')
    res = res.filter(i => i.type !== 'showdown')

    missingBoardSpots = missingBoardSpots.flat()
    res = [...res, ...missingBoardSpots, showdownSpot]
  }

  return res
}
const getAnalyzeItem = async players_hSpots => {
  if ((await gmf_fetchGmfAndReturnStatus(firstGamePoint.value?.gametype)) !== 'continue') {
    return null
  }
  const item = {
    gametype: firstGamePoint.value?.gametype,
    depth: firstGamePoint.value?.depth,
    stacks: (firstGamePoint.value?.stacks || []).join('-'),
  }
  let gameformat = getGameformat({
    item,
    gameformats: store.getters.gameformats,
  })

  // BE-DEV fix - no proper gmf on DEV yet
  if (!gameformat) {
    gameformat = store.getters.gameformats.find(i => i.gametype === firstGamePoint.value?.gametype)
  }

  let hSpots = getCompleteHistorySpots({
    history_spots: players_hSpots,
    board: board.value,
    hasFirstBoardSpot: true,
  })

  hSpots = getGroupFoldHistorySpots({
    history_spots: hSpots,
    hero_position: heroPosition.value,
    onlyRealFolds: false,
  })

  const live_solved_street = gameAnalysis.value?.live_solved_from_street
  const solved_streets = gameAnalysis.value?.solved_streets || []
  const real_streets = gameAnalysis.value?.real_streets || []
  const live_solved_depth = Number(gameAnalysis.value?.live_solved_depth)
  const solved_depth = Number(firstGamePoint.value?.depth)
  const all_solved_stacks =
    firstGamePoint.value?.stacks?.length && gamePoints.value.map(gp => gp.stacks)

  const all_boards = boards.value

  hSpots = mapHistorySpotsWithBoardDetails({
    hSpots,
    solved_streets,
    real_streets,
    live_solved_street,
    live_solved_depth,
    solved_depth,
    all_solved_stacks,
    all_boards,
    first_board: boards.value[0],
  })

  const res = {
    gameAnalysis: gameAnalysis.value,
    gameformat,
    players: players.value,
    history_spots: hSpots,
    // players_hSpots: players_hSpots.value,
    live_solved_street,
    boards: boards.value,
    opponent: opponent.value || {},
    hero,
  }

  // console.log('analyzeItem', res)
  return res
}
const getRealSolvedHistorySpots = () => {
  let res = gamePoints.value.map(gp => {
    const gpActions = gp.analysis_solved?.available_actions || []

    const heroPlayer = gp.real_game.players.find(i => i.is_hero)
    const isHeroSpot = gp.real_game.active_position === heroPlayer?.position
    const fallbackCorrectness = isHeroSpot ? 'na' : null

    let available_actions = gpActions.map(aa => {
      const rr = {
        ...aa,
        ...aa.action,
        correctness: aa.correctness || fallbackCorrectness,
        is_selected: aa.selected,
        real_raise_amount: 0,
        real_amount_by_pot: 0,
      }
      return rr
    })

    const rga = gp?.real_game_action
    const anyAction = gp?.solved_game_action || rga
    if (anyAction) {
      if (!available_actions.length) {
        const placeholderAction = {
          ...anyAction,
          real_raise_amount: Number(rga?.betsize),
          real_amount_by_pot: numFixedUnit(Number(rga?.betsize_by_pot) * 100, 'bb_pot'),
          correctness: fallbackCorrectness,
          is_selected: true,
        }
        // @ts-expect-error happened after using of orval
        available_actions = [placeholderAction]
      } else {
        available_actions = available_actions.map(aa => {
          if (aa.is_selected) {
            const r = {
              ...aa,
              real_raise_amount: Number(rga?.betsize),
              real_amount_by_pot: numFixedUnit(Number(rga?.betsize_by_pot) * 100, 'bb_pot'),
            }
            return r
          }
          return aa
        })
      }
    }

    let selected_action = available_actions.find(i => i.is_selected)

    // // BE-fix - keep this if till BE fixes missing showdown spot
    // if (selected_action && selected_action?.is_showdown !== gp?.real_game_action?.is_showdown) {
    //   selected_action.is_showdown = !!gp?.real_game_action?.is_showdown
    //   selected_action.is_hand_end = !!gp?.real_game_action?.is_hand_end
    //   selected_action.next_street = !gp?.real_game_action?.is_hand_end
    //   available_actions = available_actions.map(i => {
    //     if (i.is_selected) {
    //       i.is_showdown = !!gp?.real_game_action?.is_showdown
    //       i.is_hand_end = !!gp?.real_game_action?.is_hand_end
    //       i.next_street = !gp?.real_game_action?.is_hand_end
    //     }
    //     return i
    //   })

    //   debugger
    // }

    const r = {
      game: gp.real_game,
      available_actions,
      selected_action,
      solved_action_sequence: gp.solved_action_sequence,
    } as any

    return r
  })

  let showdownSpot = {
    game: gameAnalysis.value?.showdown,
    available_actions: [],
    selected_action: null,
  }
  res = [...res, showdownSpot]

  res = getHistorySpots({ history_spots: res, hasSelectedAction: true })

  res = getHSpotsUrlParamsFromSolvedSequece({
    history_spots: res,
    board: board.value,
    game_points: gamePoints.value,
  })

  res = setHistoryEventType({ history_spots: res })
  res = res.map(i => ({
    ...i,
    is_hero: i.type === 'player' ? i.position === heroPosition.value : false,
    is_bot: i.type === 'player' ? i.position !== heroPosition.value : false,
  }))

  // console.log('getRealSolvedHistorySpots', res)
  return res
}
const getHistoryHand = hSpots => {
  const firstHeroMove = hSpots.findIndex(
    i => i?.is_hero && (i?.available_actions || []).some(a => a?.correctness),
  )
  hSpots[firstHeroMove === -1 ? hSpots.length - 1 : firstHeroMove].is_active = true

  const correctness = getHandCorrectness({
    history_spots: hSpots,
    hero_position: heroPosition.value,
  })
  const stats_hand = getHandStats({
    history_spots: hSpots,
    hero_position: heroPosition.value,
  })

  const query = {
    ...gmfQuery.value,
    board: board.value,
    board_tmp: board.value,
    hand_tmp: heroHand.value,
    fh_hero_tmp: heroPosition.value,
    hand_op_tmp: opponentHand.value,
    fh_opponent_tmp: opponentPosition.value || '',
  }

  let showdownSpot = hSpots.find(i => i.type === 'showdown')
  const street = getStreetByBoard(board.value)
  const visibleBoards = boards.value.map(bb => getCardsTillStreet(bb, street))
  const board0 = visibleBoards[0]
  const board1 = visibleBoards[1]
  const l_board = boards.value.length === 1 ? board0 : null
  const l_boards = boards.value.length === 2 ? [board0, board1] : null

  // BE-fix - keep this if till BE fixes missing showdown spot
  if (showdownSpot) {
    hSpots = hSpots.filter(i => i.type !== 'showdown')
    showdownSpot = {
      ...showdownSpot,
      street,
      board: l_board,
      boards: l_boards,
    }
    hSpots = [...hSpots, showdownSpot]
  }

  const res = {
    query,
    history_spots: hSpots,
    correctness,
    stats_hand,
    hand_hero: heroHand.value,
    board: board.value,
    boards: boards.value,
    blinds: blinds.value,
  }
  // console.log('historyHand', res)
  return res
}
const getGameLink = ({ pathName = 'StudySolutions', spot }: { pathName?: string; spot: any }) => {
  const customQuery = {
    ...spot?.urlParams,
    ...gmfQuery.value,
    soltab: store.getters.sw_tabs_study,
    solution_type: 'gwiz',
  }
  return links_getUrl({ pathName, customQuery })
}
const getLastSpotLink = players_hSpots => {
  const firstLiveSpot = players_hSpots.find(
    i => i.type === 'player' && i.street === analyzeItem.value?.live_solved_street,
  )
  const hSpotsTillLiveStreet = players_hSpots.filter(i => {
    const r = i.id <= (firstLiveSpot?.id || 999)
    return r
  })
  const lastSolvedSpot = hSpotsTillLiveStreet[hSpotsTillLiveStreet.length - 1]
  const res = getGameLink({
    pathName: 'StudySolutions',
    spot: lastSolvedSpot,
  })
  return res
}
const setReplay = () => {
  const namespace = replayNamespace
  store.commit(`${namespace}/gamestate_set`, {
    key: 'history_spots',
    value: historyHand.value.history_spots,
  })

  store.dispatch(`${namespace}/router_update_query`, {
    query: historyHand.value.query,
    push: false,
    replace: false,
    saveLocally: false,
  })

  store.commit(`${namespace}/trainer_set`, {
    key: 'trainer_quick_result_visible',
    value: false,
  })
  store.commit(`${namespace}/trainer_set`, { key: 'trainer_result_visible', value: true })
  store.commit(`${namespace}/trainer_set`, { key: 'trainer_hand_finished', value: true })
}
const toggleReplayer = () => {
  store.commit('panels_set', { key: 'panel_replay', value: !panelReplay.value })
}

const getGmfQuery = () => {
  const gmf = analyzeItem.value?.gameformat
  const res = {
    gametype: gmf?.gametype || '',
    depth: gmf?.depth || '',
    depth_list: gmf?.depth || '',
    stacks: gmf?.stacks || '',
  }
  return res
}

const menuSpot = ref()
const menuLinks = computed(() => {
  const res = [
    {
      icon: 'mdi-school',
      id: 'solutions_spot',
      name: t('tooltips.study_spot'),
      url: getGameLink({
        pathName: 'StudySolutions',
        spot: menuSpot.value,
      }),
    },
    {
      icon: 'mdi-google-controller',
      id: 'practice_spot',
      name: t('tooltips.practice_spot'),
      url: getGameLink({
        pathName: 'PracticeTrainer',
        spot: menuSpot.value,
      }),
    },
  ]
  return res
})
const openLinksMenu = ({ e, spot }) => {
  if (spot.type === 'showdown') return
  if (spot.extra_board_spot) return
  menuSpot.value = spot

  if (handDetailMenuRef.value) {
    handDetailMenuRef.value.open(e)
  }
}

let failureHandDetailCount = 0
const isRefetching = ref(false)
const feError = ref(false)

const error = computed(() => (isError.value || feError.value) && !isRefetching.value)

// For some reason retry from useQuery is not called on 404 error so we need to do it manually
const refetchHandDetail = () => {
  if (isRefetching.value) return
  if (failureHandDetailCount < 3) {
    isRefetching.value = true
    failureHandDetailCount++
    setTimeout(() => {
      const refetchResult = refetch()
      refetchResult.then(() => {
        isRefetching.value = false
      })
    }, 1500)
  }
}

watchEffect(() => {
  if (isError.value && rqError.value?.code === 'NOT_FOUND') {
    refetchHandDetail()
  }
})

watch(
  () => isFetching.value,
  async () => {
    if (!isLoading.value && !isError.value) {
      feError.value = false
      try {
        const spots = getRealSolvedHistorySpots()
        analyzeItem.value = await getAnalyzeItem(spots)
        gmfQuery.value = getGmfQuery()
        historyHand.value = getHistoryHand(spots)
        lastSpotLink.value = getLastSpotLink(spots)
        setReplay()
      } catch (err) {
        feError.value = true
      }
    }
  },
)
</script>
<template>
  <div
    class="f-column hhv2"
    style="gap: 0.5rem; min-height: 50rem; --clr-primary: #00ac8d"
    data-tst="hand_detail"
  >
    <PortalTarget name="nav_icons" />

    <GwMessage
      v-if="warningStatus === 'ERROR'"
      :message="{
        type: 'error',
        text: $t('messages.errors.error_analyze_hand'),
      }"
      style="min-height: fit-content"
    />

    <Portal to="custom_drawer_after">
      <TrainerReplay
        v-if="analyzeItem && historyHand && !error"
        style="--clr-primary: #00ac8d"
        class="trahhitem_replay"
        :namespace="replayNamespace"
        :open="panelReplay"
        :historyHand
        :setNamespace="false"
        :hasConfirmBtns="false"
        :hasResultSwitcher="true"
        :hasSilentFetch="false"
        :hasStudySpotBtn="false"
        :hasControlIcons="mobile"
        :opponentHandOnlyOnShowdown="true"
        :loading="loading"
        :controlsIconsProps="{
          publicId: dataHand?.id,
          lastSpotLink: lastSpotLink,
        }"
      />
    </Portal>
    <HandDetailError
      v-if="error"
      :handId
    />

    <div
      v-else-if="isLoading || gmf_loading || isRefetching"
      class="mt-14 d-flex w-100 h-100"
    >
      <LoaderCircles
        size="ultra"
        class="mx-auto"
      />
    </div>

    <PartiallyAnalyzedHand
      v-if="
        !error &&
        !isFetching &&
        !(isLoading || gmf_loading) &&
        analyzeItem &&
        ['NO_GTO_SOLUTION', 'ZERO_PERCENT_ACTION', 'NEGATIVE_EV'].includes(warningStatus || '')
      "
      :warningStatus
      style="z-index: 1"
    />

    <ExpandCard
      v-if="analyzeItem && !error && !(isLoading || gmf_loading)"
      class="anim-fade-in"
      style="
        --max-height: 9999rem;
        --min-height: 3.25rem;
        --clr-back-content: var(--clr-black-1-3);
        --content-padding: 0 0.5rem;
        --radius-open: 4px;
        --radius-closed: 4px;
        --transition: 0s;
        --transition-content: 0s;
      "
      :hasScrollIntoView="false"
      :canClose="false"
    >
      <template #title>
        <HaTitle
          :correctness_group="historyHand.correctness.correctness_group"
          :hand="historyHand.hand_hero"
          :boards="historyHand.boards"
          :board="historyHand.board"
        />
      </template>
      <div
        class="ha-street-blocks"
        style="--ha-aa-width: 8rem"
      >
        <div class="ha-street-block">
          <HaControls
            :hasText="false"
            :studyLinkDisabled="warningStatus === 'ERROR'"
            :lastSpotLink
            @click:replay="toggleReplayer"
            @click:capture="capt_capture"
          >
            <GwTooltipNew
              placement="top-end"
              :variant="clipboard_copied ? 'primary-v2' : ''"
              :closeOnClick="false"
              :tooltip="
                clipboard_copied
                  ? $t('tooltips.hand_id_copied_to_clipboard')
                  : $t('tooltips.copy_hand_id_to_clipboard')
              "
            >
              <template #opener="{ listeners }">
                <div
                  class="gw_btn gw_btn_tag flex-1 csst csst_nm"
                  style="
                    --clr: var(--clr-grey-2);
                    --clr-hover: var(--clr-white-0);
                    --height: 1.5rem;
                  "
                  @click="clipboard_copyToClipboard(dataHand?.id || '')"
                  v-on="listeners"
                >
                  <div class="mdi mdi-identifier text-semi-large" />
                </div>
              </template>
            </GwTooltipNew>
          </HaControls>
        </div>

        <div
          id="capture"
          class="f-column position-relative"
          style="background: var(--clr-back-content); gap: var(--ha-gap)"
        >
          <HandDetailLogo
            v-if="capt_inProgress"
            style="
              position: absolute;
              top: 1.75rem;
              right: 1rem;
              --clr-primary: var(--app-clr-primary-v2);
            "
          />
          <div
            class="f-column"
            style="gap: var(--ha-gap)"
          >
            <div class="ha-street-block">
              <HaGameformat
                :item="{
                  date: dataHand?.date_played,
                  players: players,
                }"
                :dateConf="{
                  year: 'numeric',
                  month: 'numeric',
                  day: 'numeric',
                  hour: true,
                  minute: true,
                  second: false,
                }"
              >
                <template #title>
                  <HandDetailTitle
                    :bigBlind="Number(dataHand?.blinds?.big_blind)"
                    :smallBlind="Number(dataHand?.blinds?.small_blind)"
                    :straddle="Number(dataHand?.blinds?.straddle)"
                    :ante="Number(dataHand?.blinds?.ante)"
                    :variant="dataHand?.variant"
                    :gameformatGroup="analyzeItem.gameformat?.group"
                  />
                </template>
              </HaGameformat>

              <HaPlayers
                :players="data?.value?.players"
                :hero="hero?.position"
                :opponent="opponent?.position"
              />
            </div>
          </div>

          <AnalyzeStreetBlocks
            :item="analyzeItem"
            :showBetsIn="store.getters.show_bets_in"
            :showEvIn="store.getters.show_ev_in"
            :hasSolvedLine="true"
            :hasReal="true"
            :hasShowdown="true"
            :hasWinnings="true"
            :replayedSpotId="panelReplay ? replayedSpot.id : null"
            :hasOpenAll="!capt_inProgress"
            @click:spot="({ e, spot }) => openLinksMenu({ e, spot })"
          />
        </div>
      </div>
    </ExpandCard>
  </div>
  <GwMenuNew
    v-if="warningStatus !== 'ERROR'"
    ref="handDetailMenuRef"
    placement="contextmenu"
  >
    <template #content="{ methods }">
      <div @click="methods.close()">
        <a
          v-for="(item, index) in menuLinks"
          :key="index"
          class="gw_btn gw_btn_text text-nowrap weight_500 clr-white"
          style="--padding: 0 0.75rem"
          :href="item.url"
          target="_blank"
        >
          <div
            class="mr-2 mdi text-semi-large"
            :class="item.icon"
          ></div>
          <div class="mr-auto">
            {{ item.name }}
          </div>
        </a>
      </div>
    </template>
  </GwMenuNew>
</template>
<style lang="scss" scoped>
.trahhitem {
  &_replay {
    z-index: 1;
    --p-replay-left: var(--maximized-content-left);
  }
}
@media screen and (max-width: 1023px) {
  .trahhitem {
    &_replay {
      z-index: 4;
      --p-replay-left: 0;
    }
  }
}
</style>
